// GRADE-2 + PROD-SKU-UNICO: SKU repetido comparado do mesmo jeito que o índice único do banco
// (idx_products_sku_unique_per_company: empresa + upper(trim(sku)), produtos NÃO EXCLUÍDOS — ativos e inativos — com SKU
// preenchido). Mensagem única "SKU já cadastrado: X" (PROD-SKU-UNICO, decisão 4: com o produto dono quando a tela sabe).
import { supabase } from '@/integrations/supabase/client';
import { fetchAllRows } from '@/lib/fetchAllRows';

export const SKU_DUPLICATE_MESSAGE = 'SKU já cadastrado';

// Igual ao upper(TRIM(BOTH FROM sku)) do índice: tira só espaço nas pontas (tab e quebra de linha ficam)
const trimSpaces = (value: string): string => value.replace(/^ +| +$/g, '');
export const normalizeSku = (sku: string | null | undefined): string => trimSpaces(sku ?? '').toUpperCase();

// Escapa os coringas do LIKE (\ % _). O * também vira coringa no PostgREST: por isso a busca no banco só
// pré-seleciona e quem decide é sempre a comparação exata (normalizeSku).
const escapeLikePattern = (value: string): string => value.replace(/[\\%_]/g, (c) => `\\${c}`);

export type SkuOwner = { id: string; name: string; active: boolean };

// "SKU já cadastrado: X" e, quando se sabe quem usa, "(produto Y)" ou "(produto Y, inativo)"
export const skuDuplicateMessage = (sku: string, owner?: Pick<SkuOwner, 'name' | 'active'> | null): string => {
  const base = `${SKU_DUPLICATE_MESSAGE}: ${trimSpaces(sku)}`;
  if (!owner) return base;
  return `${base} (produto ${owner.name.trim()}${owner.active ? '' : ', inativo'})`;
};

// Recusa do próprio banco pelo índice de SKU (23505 = unique_violation)
export const isSkuUniqueViolation = (error: unknown): boolean => {
  const e = error as { code?: string; message?: string; details?: string } | null;
  return e?.code === '23505'
    && `${e?.message ?? ''} ${e?.details ?? ''}`.includes('idx_products_sku_unique_per_company');
};

// Recusa da função de criação de produto (PROD-SKU-UNICO): mensagem "SKU já cadastrado: X" pronta
const skuRefusalFromFunction = (error: unknown): string | null => {
  const message = (error as { message?: string } | null)?.message ?? '';
  return message.startsWith(`${SKU_DUPLICATE_MESSAGE}:`) ? message : null;
};

export const isSkuDuplicateError = (error: unknown): boolean =>
  isSkuUniqueViolation(error) || skuRefusalFromFunction(error) !== null;

// Texto da recusa de SKU para a tela: o da função, se veio dela; senão monta com o SKU tentado
export const skuErrorMessage = (error: unknown, sku?: string | null): string =>
  skuRefusalFromFunction(error) ?? (sku && trimSpaces(sku) ? skuDuplicateMessage(sku) : SKU_DUPLICATE_MESSAGE);

// Produto não excluído da empresa (ativo ou inativo) com o mesmo SKU (sem distinguir maiúsculas e espaços nas pontas,
// inclusive o gravado). Pré-seleciona os SKUs que contêm o digitado, lidos inteiros em lotes (sem corte). Erro é lançado.
export async function findProductWithSku(
  companyId: string,
  sku: string,
  excludeId?: string,
): Promise<SkuOwner | null> {
  const wanted = normalizeSku(sku);
  if (!wanted) return null;
  const pattern = `%${escapeLikePattern(trimSpaces(sku))}%`;
  const rows = await fetchAllRows<{ id: string; name: string; sku: string | null; active: boolean | null }>((from, to) => {
    let query = supabase
      .from('products')
      .select('id, name, sku, active')
      .eq('company_id', companyId)
      .is('deleted_at', null)
      .ilike('sku', pattern);
    if (excludeId) query = query.neq('id', excludeId);
    return query.order('id').range(from, to);
  });
  const found = rows.find((row) => normalizeSku(row.sku) === wanted);
  return found ? { id: found.id, name: found.name, active: found.active !== false } : null;
}
